import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { AvailableLanguages } from '@constants';
import { User, UserProcess, UserProcessMode, UserProcessModes } from '@interfaces';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationModalService } from '@shared/confirmation-modal/confirmation-modal.service';
import { ManagementFacade } from '@state/management';
import { ReplaySubject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-temp-premit-step5',
  templateUrl: './temp-premit-step5.component.html',
  styleUrls: ['./temp-premit-step5.component.scss'],
})
export class TempPremitStep5Component implements OnInit, AfterViewInit, OnDestroy {
  @Input() formGroup: FormGroup;
  @Input() mode: 'foreigner' | 'employee';
  @Input() user: User;
  @Input() userProcess: UserProcess;
  @Output() firstInputShiftTabPressed: EventEmitter<void> = new EventEmitter<void>();
  @Output() lastFieldTabPressed: EventEmitter<Event> = new EventEmitter<Event>();
  @Output() toggleVerificationOfPersonalDetails: EventEmitter<void> = new EventEmitter();

  public controls: { [key: string]: FormControl } = {};
  public currentLang: AvailableLanguages;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject(1);

  public UserProcessModes = UserProcessModes;
  public voivodeshipSupportedModes = [...this.UserProcessModes];

  constructor(
    private readonly translateService: TranslateService,
    private readonly confirmation: ConfirmationModalService,
    private readonly managementFacade: ManagementFacade
  ) {}

  public ngOnInit(): void {
    this.currentLang = this.translateService.currentLang as AvailableLanguages;

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(({ lang: langKey }) => {
      this.currentLang = langKey as AvailableLanguages;
    });

    Object.keys(this.formGroup.controls).forEach(fieldName => {
      this.controls[fieldName] = this.formGroup.controls[fieldName] as FormControl;
    });

    if (this.userProcess?.personalDetails?.residenceVoivodeshipDetails?.supportedModes) {
      this.voivodeshipSupportedModes = this.userProcess.personalDetails.residenceVoivodeshipDetails.supportedModes;
    }
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.hasAnyFieldAnyValue(this.formGroup.controls)) {
        this.formGroup.markAllAsTouched();
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public firstInputKeydown($event: KeyboardEvent): void {
    // interested only in situation with usage of tab
    if ($event.key?.toLowerCase() !== 'tab') {
      return;
    }

    // if only tab pressed and no family members: (noFamilyMembersInPoland === true) - emit.lastFieldTabPressed
    if (!$event.shiftKey) {
      if (this.controls.noFamilyMembersInPoland.value === true) {
        this.emitLastFieldTabPressed($event);
      }
      return;
    }

    // if shift AND tab pressed - emit.firstInputShiftTabPressed
    this.firstInputShiftTabPressed.emit();
  }

  public emitLastFieldTabPressed($event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();

    this.lastFieldTabPressed.emit($event);
  }

  // Code repetition from application-details.component because faster
  public async setUserProcessMode(modeToSet: UserProcessMode): Promise<void> {
    // allowed only for employees
    if (this.mode !== 'employee') {
      return;
    }

    const currMode = this.userProcess?.mode;
    if (!currMode || currMode === modeToSet || !this.voivodeshipSupportedModes.includes(modeToSet)) {
      return;
    }

    this.confirmation
      .open({
        confirmationToTranslate: 'COMMON.CONFIRMATION.ARE_YOU_SURE_CHANGE_USER_PROCESS_MODE',
        translateParams: { mode: this.translateService.instant('COMMON.USER_PROCESS_MODES.' + modeToSet) },
      })
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(res => {
        if (!res) {
          return;
        }

        this.managementFacade.updateUserProcessBasicData(this.userProcess.id, {
          userId: this.userProcess.userId,
          mode: modeToSet,
        });
      });
  }

  private hasAnyFieldAnyValue(controls: { [key: string]: AbstractControl }): boolean {
    return Object.keys(controls).some(controlName => {
      const val = controls[controlName].value;

      if (Array.isArray(val) && val.length > 0) {
        return true;
      }

      if (val !== '' && val !== undefined && val !== null) {
        return true;
      }

      return false;
    });
  }
}
